/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useState, useContext, useRef } from 'react';
import dayjs from 'dayjs';
import styled, { css } from 'styled-components';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { accessToken } from '@carvana/purchase-helpers';
import {
  ListIcon,
  PurchaseHeaderLogo,
  CarvanaLogoHorizontal
} from '@carvana/purchase-icons';
import LockTimer from './components/lock-timer';
import Cart from './components/cart';
import { ChatUi } from './components/chat-ui';
import ImpersonationBanner from './components/impersonation-banner';
import PurchaseMenu from './components/purchase-menu';
import { SupportContactButton, SupportMFE } from './components/supportcontact';

type PurchaseHeaderWrapperProps = {
  isMobile: boolean;
};

type PurchaseHeaderLogoProps = {
  isNativeWebview: boolean;
};

const Overlay = styled.div<{ show: boolean }>`
  position: fixed;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 98;
  display: ${props => (props.show ? 'block' : 'none')};
`;

const StickyMobileHeader = css`
  position: sticky;
  position: '-webkit-sticky';
  top: 0;
  z-index: 10;
`;

const PurchaseHeaderWrapper = styled.div<PurchaseHeaderWrapperProps>`
  width: 100%;
  background-color: #fff;
  height: 60px;
  border-bottom: 1px solid #d9dde9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  ${props => props.isMobile && StickyMobileHeader}
`;

const StyledPurchaseHeaderLogo = styled(PurchaseHeaderLogo)<
  PurchaseHeaderLogoProps
>`
  cursor: pointer;
  ${props => props.isNativeWebview && 'display: none;'}

  body.using-mouse &:focus {
    outline: none;
  }
`;

const StyledHorizontalCarvanaLogo = styled(CarvanaLogoHorizontal)`
  cursor: pointer;
  height: 36px;
  width: auto;

  body.using-mouse &:focus {
    outline: none;
  }
`;

const WidgetWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  float: right;
`;

const LeftWidgetWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 14px;
  align-items: center;
`;

const ListIconWrapper = styled.div`
  border-radius: 61px;
  border: 1px solid #d9dde9;
  width: 53px;
  height: 36px;
  padding: 8px 12px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const HeaderLogoLink = styled.a.attrs({
  href: '/'
})<{ isNativeWebview?: boolean }>`
  ${props => props?.isNativeWebview && 'visibility: hidden;'}
`;

const defaultProps = {
  hideHeader: false,
  vehicleTotalPrice: 0,
  vehicleLock: {
    isAvailable: true,
    expiresOn: dayjs().add(1, 'hour').toISOString()
  },
  navigationConfig: {
    widgetBar: { showLockTimer: true, showCart: true },
    navItems: [],
    navSections: [],
    navRail: false
  },
  vehicle: {},
  isMobile: false,
  isLoading: true
};

const PurchaseHeader = ({
  hideHeader,
  vehicleTotalPrice,
  vehicleLock,
  navigationConfig: {
    widgetBar: { showLockTimer = false, showCart = false } = {},
    navItems = [],
    navSections = [],
    navRail = false
  } = {},
  vehicle,
  isMobile,
  isLoading
} = defaultProps) => {
  if (hideHeader) return null;

  const [isNavOpen, setIsNavOpen] = useState(false);
  const { user } = accessToken();
  const isNativeWebview = /CarvanaNativeWebView/gi.test(navigator?.userAgent);
  const { trackEvent } = useContext(AnalyticsContext);
  const menuRef = useRef<HTMLDivElement>(null);

  const [isSupportMfeOpen, setIsSupportMfeOpen] = useState(false);

  useEffect(() => {
    function handleMouseDown() {
      document.body.classList.add('using-mouse');
      window.removeEventListener('mousedown', handleMouseDown);
      window.addEventListener('keydown', handleKeyDown);
    }

    function handleKeyDown() {
      document.body.classList.remove('using-mouse');
      window.removeEventListener('keydown', handleKeyDown);
      window.addEventListener('mousedown', handleMouseDown);
    }

    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (isNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isNavOpen]);

  return (
    <>
      {user?.is_impersonating?.toLowerCase() === 'true' && (
        <ImpersonationBanner user={user} />
      )}
      <PurchaseHeaderWrapper isMobile={isMobile}>
        {isMobile ? (
          <LeftWidgetWrapper>
            <HeaderLogoLink
              isNativeWebview={isNativeWebview}
              onClick={() => trackEvent('pp_clicked_logo_icon')}
            >
              <StyledPurchaseHeaderLogo />
            </HeaderLogoLink>
            <LockTimer
              vehicleLock={vehicleLock}
              display={showLockTimer}
              isMobile={isMobile}
              isModal={false}
              showCart={showCart}
            />
          </LeftWidgetWrapper>
        ) : (
          <>
            <HeaderLogoLink onClick={() => trackEvent('pp_clicked_logo_icon')}>
              <StyledHorizontalCarvanaLogo />
            </HeaderLogoLink>
            <WidgetWrapper>
              <SupportContactButton setIsOpen={setIsSupportMfeOpen} />
              <LockTimer
                vehicleLock={vehicleLock}
                display={showLockTimer}
                isMobile={isMobile}
                isModal={false}
                showCart={showCart}
              />
              <Cart
                display={showCart}
                vehicleTotalPrice={vehicleTotalPrice}
                vehicle={vehicle}
              />
            </WidgetWrapper>
          </>
        )}
        {isMobile && (
          <WidgetWrapper>
            <Cart
              display={showCart}
              vehicleTotalPrice={vehicleTotalPrice}
              vehicle={vehicle}
            />
            {!isLoading && !window.location.pathname.includes('/dashboard') && (
              <ListIconWrapper>
                <ListIcon
                  onClick={() => {
                    trackEvent('pp_clicked_list_icon');
                    setIsNavOpen(!isNavOpen);
                  }}
                />
              </ListIconWrapper>
            )}
          </WidgetWrapper>
        )}
      </PurchaseHeaderWrapper>
      <PurchaseMenu
        navItems={navItems}
        navSections={navSections}
        navRail={navRail}
        isOpen={isNavOpen}
        setIsNavOpen={setIsNavOpen}
        menuRef={menuRef}
        setIsSupportMfeOpen={setIsSupportMfeOpen}
      />
      <Overlay
        show={isNavOpen}
        onClick={() => {
          setIsNavOpen(false);
          if (menuRef.current) {
            menuRef.current.scrollTop = 0;
          }
        }}
      />
      <ChatUi />
      <SupportMFE isOpen={isSupportMfeOpen} closeAction={() => setIsSupportMfeOpen(false)} closeMobileNavMenu={() => setIsNavOpen(false)} isMobile={isMobile} />
    </>
  );
};

export default PurchaseHeader;
